import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'react-use'
import styled from 'styled-components'
import AppLayout from '~/components/appLayout'
import { trackAnalyticsEvent } from '~/utils/client/analytics'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faArrowDownAZ,
  faArrowDownZA,
  faArrowDownWideShort,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import theme from '~/design'
import { ButtonVariants } from '~/components/buttonVariants'
import PageHeader from '~/components/pageHeader'
import ContextMenu from '~/prix/react/components/contextMenu/contextMenu'
import ReportsAndStudiesCard from './reportsAndStudiesCard.component'
import { toastError, toastSuccess } from '~/components/toast'
import { Skeleton } from '~/components/skeletonLoading'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import monitorUserReportPreferencesDefinition from '~/packages/monitor/userReportPreferences/monitorUserReportPreferences.definition'

type ReportsAndStudiesSortOption = 'initial' | 'titleAscending' | 'titleDescending'

type ReportsAndStudiesItem = {
  title: string
  imageSource?: string
  imageAlternativeText: string
  description: string
  destinationPath: string
}

type MonitorUserReportPreference = {
  destinationPath: string
  isFixed: boolean
  ordinalPosition?: number
}

const shortcutPlaceholderImage = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360">
  <rect width="640" height="360" fill="#e0e5eb"/>
  <path d="M110 260L244 178L368 224L530 126V302H110V260Z" fill="#ffffff"/>
  <path d="M110 260L244 178L368 224L530 126" fill="none" stroke="#58c4da" stroke-width="18" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M330 78C330 50 352 28 380 28C408 28 430 50 430 78C430 120 380 172 380 172C380 172 330 120 330 78Z" fill="#0f438a"/>
  <circle cx="380" cy="78" r="18" fill="#ffffff"/>
  <circle cx="110" cy="260" r="14" fill="#0f438a"/>
  <circle cx="244" cy="178" r="14" fill="#0f438a"/>
  <circle cx="368" cy="224" r="14" fill="#0f438a"/>
  <circle cx="530" cy="126" r="14" fill="#0f438a"/>
</svg>
`)}`

const reportsAndStudiesItems: ReportsAndStudiesItem[] = [
  {
    title: 'Relatório Padrão de Monitoramento de Portfólios',
    imageSource:
      'https://images.unsplash.com/photo-1711097383282-28097ae16b1d?auto=format&fit=crop&fm=jpg&q=80&w=1200',
    imageAlternativeText: 'Painel de dados',
    description:
      'Visão inicial dos principais resultados dos portfólios de soluções e seus indicadores.',
    destinationPath: '/app/report/standard-portfolio-report',
  },
  {
    title: 'Monitoramento das Integrações dos Atendimentos',
    imageSource:
      'https://images.unsplash.com/photo-1516728043722-b394cb2f689e?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    imageAlternativeText: 'Fluxo de monitoramento',
    description: 'Acompanhamento da última data de atendimento por tipo de serviço integrado.',
    destinationPath: '/app/report/service-integrations-monitoring',
  },
  // {
  //   title: 'Média do faturamento mensal dos pequenos negócios',
  //   imageSource: '/report_assets/monthly_revenue_average.png',
  //   imageAlternativeText: 'Gráfico de faturamento',
  //   description: 'Comparação das distribuições por frequência, engajamento e período.',
  //   destinationPath: '/app/external-report/media-do-faturamento-mensal-dos-pequenos-negocios',
  // },
  {
    title: 'Atendimentos na visão do funil de oferta',
    imageAlternativeText: 'Funil de oferta',
    description:
      'Visualização do funil de oferta, com a quantidade de atendimentos por etapa e por tipo de serviço.',
    destinationPath: '/app/external-report/atendimentos-na-visao-do-funil-de-oferta',
  },
  {
    title: 'Ciclo de Vida do Produto',
    imageSource: '/report_assets/life_cycle.png',
    imageAlternativeText: 'Ciclo de vida do produto',
    description:
      'Um estudo sobre ciclo de vida do produto, aplicado sobre os cursos do portfólio EAD.',
    destinationPath: '/app/report/course-life-cycle',
  },
  {
    title: 'Aglomerados Subnormais',
    imageSource:
      'https://images.unsplash.com/photo-1551717256-ad2ac9ab0261?blend=000000&blend-alpha=10&blend-mode=normal&blend-w=1&crop=faces%2Cedges&h=630&mark=https%3A%2F%2Fimages.unsplash.com%2Fopengraph%2Flogo.png&mark-align=top%2Cleft&mark-pad=50&mark-w=64&w=1200&auto=format&fit=crop&q=60&ixid=M3wxMjA3fDB8MXxhbGx8fHx8fHx8fHwxNzEwNTM2Mzk0fA&ixlib=rb-4.0.3',
    imageAlternativeText: 'Vista urbana em aglomerado',
    description:
      'Um estudo sobre alguns aglomerados subnormais do Brasil, seus estabelecimentos e atendimentos pelo SEBRAE.',
    destinationPath: '/app/report/subnormal-clusters',
  },
  {
    title: 'Evasão de Cursos',
    imageSource:
      'https://images.unsplash.com/photo-1502465771179-51f3535da42c?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    imageAlternativeText: 'Ambiente de estudo',
    description: 'Um estudo sobre evasão de cursos a nível nacional e estadual.',
    destinationPath: '/app/report/course-dropout',
  },
  {
    title: 'Roteiros de atendimentos',
    imageSource:
      'https://images.unsplash.com/photo-1594935975218-a3596da034a3?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bWFwYSUyMGRhJTIwcm90YXxlbnwwfHwwfHx8MA%3D%3D',
    imageAlternativeText: 'Mapa de rota',
    description:
      'Estudo com foco em recomendações de empresas, leitura do histórico do agente e visualização do roteiro sugerido.',
    destinationPath: '/app/report/agent-advised-roadmaps',
  },
  {
    title: 'Atendimento Desproporcional',
    imageSource: '/report_assets/disproportionate_attention.png',
    imageAlternativeText: 'Marcação de localização',
    description:
      'Atalho para analisar concentrações de atendimentos e identificar territórios com comportamento desproporcional.',
    destinationPath: '/app/disproportionate-attention',
  },
  {
    title: 'Mapa de Demarcação',
    imageSource: '/report_assets/demarcation_map.png',
    imageAlternativeText: 'Marcação de localização',
    description:
      'Atalho para desenhar áreas no mapa e consultar estabelecimentos dentro de regiões demarcadas.',
    destinationPath: '/app/map/demarcation',
  },
  {
    title: 'Relatório do Agente',
    imageSource: '/report_assets/agent_report.png',
    imageAlternativeText: 'Marcação de localização',
    description:
      'Atalho para acompanhar a produção dos agentes e visualizar recortes de atendimento por território.',
    destinationPath: '/app/agent-report',
  },
  {
    title: 'Séries Temporais',
    imageSource: '/report_assets/time_series.png',
    imageAlternativeText: 'Marcação de localização',
    description:
      'Atalho para consultar indicadores ao longo do tempo e observar tendências por tema ou território.',
    destinationPath: '/app/time-series/',
  },
  {
    title: 'Outros Mapas',
    imageSource: '/report_assets/other_maps.png',
    imageAlternativeText: 'Marcação de localização',
    description: 'Atalho para acessar os demais mapas disponíveis para exploração geográfica.',
    destinationPath: '/app/map/',
  },
]

const reportsAndStudiesSortOptionStorageKey = 'reportsAndStudiesSortOption'
const defaultReportsAndStudiesSortOption: ReportsAndStudiesSortOption = 'initial'
const reportsAndStudiesSortOptions: ReportsAndStudiesSortOption[] = [
  'initial',
  'titleAscending',
  'titleDescending',
]
const reportsAndStudiesDestinationPathSet = new Set(
  reportsAndStudiesItems.map(item => item.destinationPath),
)

function normalizeSearchText(text: string) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
}

function getReportsAndStudiesSortOption(sortOption: ReportsAndStudiesSortOption | undefined) {
  return sortOption && reportsAndStudiesSortOptions.includes(sortOption)
    ? sortOption
    : defaultReportsAndStudiesSortOption
}

function getFixedReportsAndStudiesDestinationPaths(preferences: MonitorUserReportPreference[]) {
  return preferences
    .filter(preference => preference.isFixed)
    .sort((firstPreference, secondPreference) => {
      const firstPosition = firstPreference.ordinalPosition ?? Number.MAX_SAFE_INTEGER
      const secondPosition = secondPreference.ordinalPosition ?? Number.MAX_SAFE_INTEGER

      if (firstPosition !== secondPosition) {
        return firstPosition - secondPosition
      }

      return firstPreference.destinationPath.localeCompare(
        secondPreference.destinationPath,
        'pt-BR',
      )
    })
    .map(preference => preference.destinationPath)
    .filter(destinationPath => reportsAndStudiesDestinationPathSet.has(destinationPath))
}

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex: 1;
  }
`

const Input = styled.input`
  padding: 0.75rem;
  padding-left: ${({ theme }) => theme.padding(3)};
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  font-size: 16px;
  width: 100%;
  min-width: 270px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.darkGrey};
  }
`

const InputIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: ${({ theme }) => theme.padding(1)};
  z-index: 1;
  width: 18px;
  height: 18px;
`

const WrapperFilter = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: stretch;
`

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: stretch;
  padding: 24px;
`

const Content = styled.main<{ $hasVisibleItems: boolean }>`
  width: 100%;
  max-width: 1680px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  align-content: ${({ $hasVisibleItems }) => ($hasVisibleItems ? 'start' : 'stretch')};
  gap: 24px;
  margin: 0 auto;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1160px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  @media (min-width: 1720px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: 32px;
  color: ${({ theme }) => theme.colors.darkGrey};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  h3 {
    font-size: 16px;
    font-weight: 500;
  }
`

function sortReportsAndStudiesItems(
  items: ReportsAndStudiesItem[],
  sortOption: ReportsAndStudiesSortOption,
  fixedReportsAndStudiesDestinationPaths: string[],
) {
  const sortedItems =
    sortOption === 'initial'
      ? items
      : [...items].sort((firstItem, secondItem) => {
          const comparison = firstItem.title.localeCompare(secondItem.title, 'pt-BR')

          return sortOption === 'titleAscending' ? comparison : -comparison
        })

  if (fixedReportsAndStudiesDestinationPaths.length === 0) {
    return sortedItems
  }

  const fixedReportsAndStudiesDestinationPathSet = new Set(fixedReportsAndStudiesDestinationPaths)

  return [...sortedItems].sort((firstItem, secondItem) => {
    const isFirstItemFixed = fixedReportsAndStudiesDestinationPathSet.has(firstItem.destinationPath)
    const isSecondItemFixed = fixedReportsAndStudiesDestinationPathSet.has(
      secondItem.destinationPath,
    )

    if (isFirstItemFixed === isSecondItemFixed) {
      return 0
    }

    return isFirstItemFixed ? -1 : 1
  })
}

export default function ReportsAndStudiesScreen() {
  const [showFilter, setShowFilter] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [storedSortOption, setSortOption] = useLocalStorage<ReportsAndStudiesSortOption>(
    reportsAndStudiesSortOptionStorageKey,
    defaultReportsAndStudiesSortOption,
  )
  const sortOption = getReportsAndStudiesSortOption(storedSortOption)
  const [fixedReportsAndStudiesDestinationPaths, setFixedReportsAndStudiesDestinationPaths] =
    useState<string[]>([])
  const [
    changingReportsAndStudiesFixedStateDestinationPaths,
    setChangingReportsAndStudiesFixedStateDestinationPaths,
  ] = useState<string[]>([])
  const [hasLoadedReportPreferences, setHasLoadedReportPreferences] = useState(false)
  const { callAction: callMonitorUserReportPreferences } = useActionFunction(
    monitorUserReportPreferencesDefinition,
    {
      initialIsLoading: false,
    },
  )

  const fixedReportsAndStudiesDestinationPathSet = useMemo(
    () => new Set(fixedReportsAndStudiesDestinationPaths),
    [fixedReportsAndStudiesDestinationPaths],
  )

  const isLoading = !hasLoadedReportPreferences

  const loadReportsAndStudiesPreferences = useCallback(async () => {
    try {
      const result = await callMonitorUserReportPreferences({}).resultPromise

      setFixedReportsAndStudiesDestinationPaths(
        getFixedReportsAndStudiesDestinationPaths(result.items),
      )
    } catch (error) {
      console.error(error)
      // toastError('Não foi possível carregar os relatórios e estudos fixados.')
    } finally {
      setHasLoadedReportPreferences(true)
    }
  }, [callMonitorUserReportPreferences])

  useEffect(() => {
    loadReportsAndStudiesPreferences().catch(error => console.error(error))
  }, [loadReportsAndStudiesPreferences])

  const changeReportsAndStudiesFixedState = useCallback(
    async (destinationPath: string, isFixed: boolean) => {
      if (!reportsAndStudiesDestinationPathSet.has(destinationPath)) {
        return
      }

      const previousFixedReportsAndStudiesDestinationPaths = fixedReportsAndStudiesDestinationPaths
      const nextFixedReportsAndStudiesDestinationPaths = isFixed
        ? previousFixedReportsAndStudiesDestinationPaths.includes(destinationPath)
          ? previousFixedReportsAndStudiesDestinationPaths
          : [...previousFixedReportsAndStudiesDestinationPaths, destinationPath]
        : previousFixedReportsAndStudiesDestinationPaths.filter(
            currentDestinationPath => currentDestinationPath !== destinationPath,
          )

      setChangingReportsAndStudiesFixedStateDestinationPaths(currentDestinationPaths =>
        currentDestinationPaths.includes(destinationPath)
          ? currentDestinationPaths
          : [...currentDestinationPaths, destinationPath],
      )

      try {
        const result = await callMonitorUserReportPreferences({
          preferences: [
            {
              destinationPath,
              isFixed,
              ordinalPosition: isFixed
                ? nextFixedReportsAndStudiesDestinationPaths.indexOf(destinationPath)
                : undefined,
            },
          ],
        }).resultPromise

        setFixedReportsAndStudiesDestinationPaths(
          getFixedReportsAndStudiesDestinationPaths(result.items),
        )

        toastSuccess(`O relatório ou estudo foi ${isFixed ? 'fixado' : 'desafixado'} com sucesso.`)
      } catch (error) {
        console.error(error)
        setFixedReportsAndStudiesDestinationPaths(previousFixedReportsAndStudiesDestinationPaths)
        toastError(`Não foi possível ${isFixed ? 'fixar' : 'desafixar'} o relatório ou estudo.`)
      } finally {
        setChangingReportsAndStudiesFixedStateDestinationPaths(currentDestinationPaths =>
          currentDestinationPaths.filter(
            currentDestinationPath => currentDestinationPath !== destinationPath,
          ),
        )
      }
    },
    [callMonitorUserReportPreferences, fixedReportsAndStudiesDestinationPaths],
  )

  const visibleReportsAndStudiesItems = useMemo(() => {
    const normalizedSearchText = normalizeSearchText(searchText.trim())
    const filteredItems =
      normalizedSearchText.length === 0
        ? reportsAndStudiesItems
        : reportsAndStudiesItems.filter(item => {
            const normalizedItemText = normalizeSearchText(`${item.title} ${item.description}`)

            return normalizedItemText.includes(normalizedSearchText)
          })

    return sortReportsAndStudiesItems(
      filteredItems,
      sortOption,
      fixedReportsAndStudiesDestinationPaths,
    )
  }, [fixedReportsAndStudiesDestinationPaths, searchText, sortOption])

  const ListSkeleton = useMemo(() => {
    const skeletons = []

    for (let i = 0; i < reportsAndStudiesItems.length; i++) {
      skeletons.push(
        <Skeleton
          key={i}
          style={{
            minHeight: 320,
            height: '100%',
          }}
        />,
      )
    }

    return skeletons
  }, [])

  useEffect(() => {
    trackAnalyticsEvent('study_list_opened', {
      source_screen: 'study_list',
    })
  }, [])

  return (
    <AppLayout title='Relatórios e Estudos' dockActive='reports-and-studies'>
      <PageHeader
        title='Relatórios e Estudos'
        subtitle='Encontre abaixo relatórios e estudos para consulta.'
      >
        <InputWrapper>
          <InputIcon icon={faSearch} color={theme.colors.darkGrey} />
          <Input
            aria-label='Pesquisar relatório ou estudo'
            value={searchText}
            placeholder='Pesquisar relatório ou estudo'
            onChange={event => setSearchText(event.target.value)}
          />
        </InputWrapper>
        <WrapperFilter>
          <ButtonVariants
            title='Ordenar'
            aria-label='Ordenar relatórios e estudos'
            icon={
              sortOption === 'titleAscending'
                ? faArrowDownAZ
                : sortOption === 'titleDescending'
                  ? faArrowDownZA
                  : faArrowDownWideShort
            }
            size='lg'
            backgroundColor='transparent'
            color={theme.colors.darkGrey}
            onClick={() => setShowFilter(previousShowFilter => !previousShowFilter)}
          />
          {showFilter && (
            <ContextMenu
              xBasis='right'
              yBasis='top'
              style={{ top: '100%' }}
              header='Ordenar por:'
              arrow={{ position: 'top', x: 200 }}
              onExit={() => setShowFilter(false)}
              functionalities={[
                {
                  run: () => setSortOption('initial'),
                  title: 'Ordem inicial',
                  icon: <FontAwesomeIcon icon={faArrowDownWideShort} />,
                },
                {
                  run: () => setSortOption('titleAscending'),
                  title: 'Ordem alfabética',
                  icon: <FontAwesomeIcon icon={faArrowDownAZ} />,
                },
                {
                  run: () => setSortOption('titleDescending'),
                  title: 'Ordem alfabética reversa',
                  icon: <FontAwesomeIcon icon={faArrowDownZA} />,
                },
              ]}
            />
          )}
        </WrapperFilter>
      </PageHeader>
      <Wrapper>
        <Content $hasVisibleItems={visibleReportsAndStudiesItems.length > 0}>
          {isLoading ? (
            ListSkeleton
          ) : visibleReportsAndStudiesItems.length > 0 ? (
            visibleReportsAndStudiesItems.map(item => (
              <ReportsAndStudiesCard
                key={item.destinationPath}
                title={item.title}
                imageSource={item.imageSource ?? shortcutPlaceholderImage}
                imageAlternativeText={item.imageAlternativeText}
                description={item.description}
                destinationPath={item.destinationPath}
                isFixed={fixedReportsAndStudiesDestinationPathSet.has(item.destinationPath)}
                isChangingFixedState={changingReportsAndStudiesFixedStateDestinationPaths.includes(
                  item.destinationPath,
                )}
                onChangeFixedState={isFixed => {
                  void changeReportsAndStudiesFixedState(item.destinationPath, isFixed)
                }}
              />
            ))
          ) : (
            <EmptyState>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                color={theme.colors.mediumGrey}
                size='5x'
              />
              <h3>Nenhum relatório ou estudo encontrado.</h3>
            </EmptyState>
          )}
        </Content>
      </Wrapper>
    </AppLayout>
  )
}
